import React, {type BaseSyntheticEvent, useReducer, useRef} from 'react';
import styles from './EnterDetailsStep.module.css';
import {
    Button,
    Typography,
    Stack,
    RadioGroup,
    Radio,
    FormControlLabel,
    Grid,
    Alert,
    CircularProgress,
    Box
} from "@mui/material";
import {GroupOfPeople2} from "./Icons/GroupOfPeople2";
import {GroupOfPeople3} from "./Icons/GroupOfPeople3";
import {GroupOfPeople4} from "./Icons/GroupOfPeople4";
import {GroupOfPeople5} from "./Icons/GroupOfPeople5";
import {Form} from "@base-ui-components/react";
import Tooltip from '../Tooltip/Tooltip'
import {useText} from "../../main.tsx";
import {trackDetailsSubmitted} from "../../utils/funnelTracking.ts";
import type {PowerConsumptionPlan} from "../../types/solar";
import {useProject} from "../../context/ProjectContext.tsx";
import type {SlimmedProjectGetDataResponse} from "@shared/interfaces/solar";
import {
    enterDetailsStepReducer,
    initialEnterDetailsStepState,
} from "./EnterDetailsStep.reducer";
import {useAddressData, useFunnelStep} from "../../context/FunnelDataContext";
import {FUNNEL_STEP_IDS} from "../../constants/funnelSteps";
import {createLogger} from "../../lib/logger.ts";

const logger = createLogger("EnterDetailsStep");

const powerConsumptionEstimateValues: PowerConsumptionPlan[] = [
    {
        id: '1-2',
        label: '1-2 Personen',
        icon: <GroupOfPeople2 sx={{fontSize: 80}}></GroupOfPeople2>,
        value: 3000
    },
    {
        id: '3',
        label: '3 Personen',
        icon: <GroupOfPeople3 sx={{fontSize: 80}}></GroupOfPeople3>,
        value: 3750
    },
    {
        id: '4',
        label: '4 Personen',
        icon: <GroupOfPeople4 sx={{fontSize: 80}}></GroupOfPeople4>,
        value: 4500
    },
    {
        id: '5+',
        label: '5+ Personen',
        icon: <GroupOfPeople5 sx={{fontSize: 80}}></GroupOfPeople5>,
        value: 7000
    }
];

export type EnterDetailsStepData = {
    selectedPowerConsumptionEstimate: PowerConsumptionPlan;
};

const EnterDetailsStep: React.FC = () => {
    const { data: enterDetailsData, submit: onSubmit } =
        useFunnelStep<EnterDetailsStepData>(FUNNEL_STEP_IDS.DETAILS_INPUT);
    const addressStepData = useAddressData()!;
    const initialPowerConsumption = enterDetailsData?.selectedPowerConsumptionEstimate || powerConsumptionEstimateValues[0];
    
    const [state, dispatch] = useReducer(
        enterDetailsStepReducer,
        initialPowerConsumption,
        initialEnterDetailsStepState
    );

    const {t} = useText();
    const {loadProjectByAddress, pollProjectUntilTerminal, isPolling, error: contextError} = useProject();

    // Ref so async callbacks always read the latest context error without stale-closure issues
    const contextErrorRef = useRef<string | null>(contextError);
    contextErrorRef.current = contextError;

    const handleChange = (e: BaseSyntheticEvent) => {
        const newPowerConsumptionEstimate = powerConsumptionEstimateValues.find(opt => opt.id === e.target.value);

        logger.debug('New power consumption estimation', { newPowerConsumptionEstimate });

        if (newPowerConsumptionEstimate) {
            // Local selection only; the chosen plan is committed to funnel data on
            // submit. Committing live would erase the baseline used to detect a real
            // plan change (and thus whether to override a manual configurator value).
            dispatch({
                type: "SELECT_POWER_CONSUMPTION",
                payload: newPowerConsumptionEstimate,
            });
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!state.selectedPowerConsumption) {
            return;
        }

        // Load or create project via ProjectContext; catch here so loading errors
        // are surfaced in the component's own error state rather than as unhandled
        // promise rejections.
        let project: Awaited<ReturnType<typeof loadProjectByAddress>>;
        try {
            project = await loadProjectByAddress(addressStepData.currentAddress);
        } catch (err) {
            logger.error('Error loading project', { error: err });
            dispatch({
                type: "LOADING_ERROR",
                payload: err instanceof Error ? err.message : "Kein Projekt verfügbar. Bitte starten Sie mit der Adresseingabe.",
            });
            return;
        }

        if (project.state === 'ERROR') {
            if (project.error) {
                dispatch({
                    type: "LOADING_ERROR",
                    payload: project.error.message,
                });
                return;
            }
            const projectData = project.data as SlimmedProjectGetDataResponse;
            if (projectData?.rooftopError) {
                switch (projectData.rooftopError.responseBody.errorMessage.code) {
                    case 'HOUSE_NOT_LOCATED':
                        dispatch({
                            type: "LOADING_ERROR",
                            payload: t('errorCodes.HOUSE_NOT_LOCATED') || 'Adresse nicht gefunden',
                        });
                        break;
                }
                return;
            }
            dispatch({
                type: "LOADING_ERROR",
                payload: "Unknown error",
            });
            logger.debug('project.data', { data: project.data });
            return;
        }

        const projectData = project.data as SlimmedProjectGetDataResponse;

        if (project.state === "COMPLETED") {
            if (projectData?.confirmedRooftopData && projectData?.confirmedPanelsData) {
                trackDetailsSubmitted(state.selectedPowerConsumption.id, state.selectedPowerConsumption.value);
                onSubmit({selectedPowerConsumptionEstimate: state.selectedPowerConsumption});
                return;
            }
        }

        dispatch({ type: "START_LOADING" });

        try {
            logger.debug('Project data', { projectData });

            dispatch({ type: "START_POLLING" });
            const result = await pollProjectUntilTerminal();

            switch (result) {
                case 'COMPLETED':
                    dispatch({ type: "POLLING_COMPLETED" });
                    trackDetailsSubmitted(state.selectedPowerConsumption.id, state.selectedPowerConsumption.value);
                    onSubmit({selectedPowerConsumptionEstimate: state.selectedPowerConsumption});
                    break;
                case 'TIMEOUT':
                    dispatch({ type: "POLLING_TIMEOUT" });
                    break;
                case 'ERROR':
                default:
                    dispatch({
                        type: "POLLING_ERROR",
                        payload: project.error?.message || contextErrorRef.current || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
                    });
                    break;
            }
        } catch (err) {
            logger.error('Error in handleSubmit', { error: err });
            dispatch({
                type: "POLLING_ERROR",
                payload: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
            });
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Stack direction={"column"} spacing={3} sx={{
                alignItems: "center",
            }}>
                <Typography variant={"h1"}>
                    {t('funnel.enterDetailsStep.title')}
                    <Tooltip text={t('funnel.enterDetailsStep.tooltip') || ''}/>
                </Typography>

                <RadioGroup row value={state.selectedPowerConsumption.value} onChange={handleChange} sx={{
                    gap: 1
                }}>
                    <Grid container spacing={2} sx={{
                        p: 0,
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap",
                    }}>
                        {powerConsumptionEstimateValues.map((option) => (
                            <Grid key={option.id} size={{xs: 6, sm: 3}}>
                                <FormControlLabel
                                    key={option.id}
                                    className={`${styles.radioOption} ${state.selectedPowerConsumption.id === option.id ? styles.selected : ''}`}
                                    control={<Radio disableRipple icon={option.icon}/>}
                                    value={option.id}
                                    label={option.label}
                                    labelPlacement="bottom"
                                    sx={{mx: 0}}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </RadioGroup>

                {state.error && <Alert severity={state.errorSeverity ?? "error"}><span dangerouslySetInnerHTML={{ __html: t('funnel.enterDetailsStep.error')! }} /></Alert>}

                {(state.isPolling || isPolling()) && (
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
                        <CircularProgress/>
                        <Typography variant="body1" sx={{textAlign: 'center'}}>
                            Ihre Dachanalyse wird verarbeitet. Bitte warten Sie einen Moment...
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center'}}>
                            Dies kann bis zu zwei Minuten dauern.
                        </Typography>
                    </Box>
                )}

                <div className={styles.buttonContainer}>
                    <Button
                        variant={"contained"}
                        type={"submit"}
                        size={"large"}
                        loading={state.isLoading && !state.isPolling}
                        disabled={state.isPolling || isPolling() || state.error !== null}
                        sx={{
                            borderRadius: '0.5rem',
                            px: "2.5rem",
                            py: "1rem",
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            inlineSize: 'fit-content',
                            whiteSpace: 'nowrap',
                        }}>
                        {(state.isPolling || isPolling()) ? 'Wird verarbeitet...' : 'Weiter'}
                    </Button>
                </div>
            </Stack>
        </Form>
    );
};

export default EnterDetailsStep;
